<template>
  <div class="ad-page">
    <p v-if="loadError" class="ad-error">{{ loadError }}</p>

    <!-- Indicadores -->
    <section class="kpis">
      <router-link class="ad-card metric metric--link" to="/superadmin/parques">
        <span class="metric__label">Parques activos</span>
        <span class="metric__value metric__value--display" style="color: #1e3a5f">{{ data ? data.kpis.activeParks : '—' }}</span>
        <span class="metric__sub">{{ data ? `de ${data.kpis.totalParks} registrados` : '' }}</span>
      </router-link>
      <router-link class="ad-card metric metric--link" to="/superadmin/facturacion">
        <span class="metric__label">MRR</span>
        <span class="ad-mono metric__value" style="color: #10b981">{{ data ? money(data.kpis.mrr) : '—' }}</span>
        <span class="metric__sub">Parques activos con plan</span>
      </router-link>
      <router-link class="ad-card metric metric--link" to="/superadmin/facturacion">
        <span class="metric__label">Ingresos del año</span>
        <span class="ad-mono metric__value" style="color: #1e3a5f">{{ data ? money(data.kpis.yearIncome) : '—' }}</span>
        <span class="metric__sub">Pagos registrados este año</span>
      </router-link>
      <router-link class="ad-card metric metric--link" to="/superadmin/facturacion">
        <span class="metric__label">Churn del mes</span>
        <span class="metric__value metric__value--display" :style="{ color: churnColor }">{{ data ? `${data.kpis.churn.percent}%` : '—' }}</span>
        <span class="metric__sub">
          <template v-if="data">{{ data.kpis.churn.churned }} de {{ data.kpis.churn.base }} parques</template>
        </span>
      </router-link>
      <router-link class="ad-card metric metric--link" to="/superadmin/facturacion">
        <span class="metric__label">Pagos vencidos</span>
        <span class="metric__value metric__value--display" :style="{ color: data && data.kpis.overdueCount ? '#e84545' : '#10b981' }">{{ data ? data.kpis.overdueCount : '—' }}</span>
        <span class="metric__sub">{{ data && data.kpis.overdueCount ? 'Requieren seguimiento' : 'Todo al corriente' }}</span>
      </router-link>
    </section>

    <!-- Gráfica de 12 meses -->
    <section class="ad-card chart-card">
      <header class="chart-head">
        <h2 class="sec-title">Últimos 12 meses</h2>
        <div class="legend">
          <span class="legend__item"><i class="legend__swatch legend__swatch--bar"></i>MRR</span>
          <span class="legend__item"><i class="legend__swatch legend__swatch--line"></i>Parques activos</span>
        </div>
      </header>
      <p v-if="!data" class="ad-empty">Cargando…</p>
      <template v-else>
        <svg class="chart" :viewBox="`0 0 ${W} ${H}`" role="img" aria-label="MRR y parques activos por mes">
          <!-- cuadrícula y eje de MRR (izquierda) -->
          <g v-for="t in ticks" :key="t.i">
            <line :x1="PAD_L" :x2="W - PAD_R" :y1="t.y" :y2="t.y" class="grid" />
            <text :x="PAD_L - 8" :y="t.y + 3" text-anchor="end" class="axis">{{ shortMoney(t.mrr) }}</text>
            <text :x="W - PAD_R + 8" :y="t.y + 3" text-anchor="start" class="axis axis--parks">{{ t.parks }}</text>
          </g>
          <!-- barras de MRR -->
          <rect v-for="b in bars" :key="b.month" :x="b.x" :y="b.y" :width="b.w" :height="b.h" rx="3" class="bar">
            <title>{{ b.label }}: {{ money(b.value) }}</title>
          </rect>
          <!-- línea de parques activos (se corta donde no hay foto) -->
          <path v-for="(seg, i) in linePaths" :key="i" :d="seg" class="line" />
          <g v-for="p in points" :key="p.month">
            <circle :cx="p.x" :cy="p.y" r="3.5" class="dot"><title>{{ p.label }}: {{ p.value }} parques activos</title></circle>
          </g>
          <text v-for="m in monthLabels" :key="m.month" :x="m.x" :y="H - 8" text-anchor="middle" class="axis">{{ m.label }}</text>
        </svg>
        <p v-if="!hasAnyData" class="ad-empty">Aún no hay datos.</p>
        <p v-else-if="missingCount" class="ad-hint chart-note">
          La gráfica se alimenta desde que se activó el Dashboard: los meses anteriores aparecen vacíos.
        </p>
      </template>
    </section>

    <!-- Actividad reciente -->
    <section class="ad-card">
      <header class="chart-head">
        <h2 class="sec-title">Actividad reciente</h2>
        <router-link class="see-all" to="/superadmin/bitacora">Ver bitácora</router-link>
      </header>
      <p v-if="!data" class="ad-empty">Cargando…</p>
      <p v-else-if="!data.activity.length" class="ad-empty">Todavía no hay actividad registrada.</p>
      <ul v-else class="activity">
        <li v-for="a in data.activity" :key="a.id" class="activity__row">
          <div class="activity__main">
            <span class="activity__summary">{{ a.summary }}</span>
            <span class="activity__meta">{{ a.superAdminName }}<template v-if="a.parkName"> · {{ a.parkName }}</template></span>
          </div>
          <span class="activity__time" :title="formatDateTime(a.createdAt)">{{ relativeTime(a.createdAt) }}</span>
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getDashboard } from '@/services/superadmin'
import { money, relativeTime, formatDateTime, errorText } from '@/components/Superadmin/adminFormat'

const data = ref(null)
const loadError = ref('')

const churnColor = computed(() => (data.value && data.value.kpis.churn.percent > 3 ? '#e84545' : '#10b981'))

// ---- Gráfica (SVG a mano: sin librerías) ----
const W = 760
const H = 260
const PAD_L = 64
const PAD_R = 40
const PAD_T = 16
const PAD_B = 30
const innerW = W - PAD_L - PAD_R
const innerH = H - PAD_T - PAD_B

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']
const labelOf = (ym) => {
  const [y, m] = ym.split('-').map(Number)
  return `${MONTHS[m - 1]} ${String(y).slice(2)}`
}

// Escala "bonita" hacia arriba para que el eje no quede en números raros.
function niceMax(v) {
  if (v <= 0) return 1
  const pow = 10 ** Math.floor(Math.log10(v))
  const n = v / pow
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10
  return step * pow
}

const history = computed(() => (data.value ? data.value.history : []))
const hasAnyData = computed(() => history.value.some((h) => h.mrr !== null))
const missingCount = computed(() => history.value.filter((h) => h.mrr === null).length)
const maxMrr = computed(() => niceMax(Math.max(0, ...history.value.map((h) => h.mrr || 0))))
const maxParks = computed(() => Math.max(4, niceMax(Math.max(0, ...history.value.map((h) => h.activeParks || 0)))))

const slot = computed(() => innerW / Math.max(1, history.value.length))
const xCenter = (i) => PAD_L + slot.value * i + slot.value / 2
const yMrr = (v) => PAD_T + innerH - (v / maxMrr.value) * innerH
const yParks = (v) => PAD_T + innerH - (v / maxParks.value) * innerH

const ticks = computed(() =>
  [0, 1, 2, 3, 4].map((i) => ({
    i,
    y: PAD_T + innerH - (innerH / 4) * i,
    mrr: (maxMrr.value / 4) * i,
    parks: Math.round((maxParks.value / 4) * i * 10) / 10,
  })),
)

const bars = computed(() => {
  const w = Math.min(34, slot.value * 0.55)
  return history.value
    .map((h, i) => ({ h, i }))
    .filter(({ h }) => h.mrr !== null && h.mrr > 0)
    .map(({ h, i }) => ({
      month: h.month, label: labelOf(h.month), value: h.mrr,
      x: xCenter(i) - w / 2, w, y: yMrr(h.mrr), h: PAD_T + innerH - yMrr(h.mrr),
    }))
})

const points = computed(() =>
  history.value
    .map((h, i) => ({ h, i }))
    .filter(({ h }) => h.activeParks !== null)
    .map(({ h, i }) => ({ month: h.month, label: labelOf(h.month), value: h.activeParks, x: xCenter(i), y: yParks(h.activeParks) })),
)

// Un trazo por tramo continuo de meses con datos.
const linePaths = computed(() => {
  const segs = []
  let cur = []
  history.value.forEach((h, i) => {
    if (h.activeParks === null) {
      if (cur.length) segs.push(cur)
      cur = []
    } else {
      cur.push(`${xCenter(i).toFixed(1)} ${yParks(h.activeParks).toFixed(1)}`)
    }
  })
  if (cur.length) segs.push(cur)
  return segs.filter((s) => s.length > 1).map((s) => `M ${s.join(' L ')}`)
})

const monthLabels = computed(() => history.value.map((h, i) => ({ month: h.month, label: labelOf(h.month), x: xCenter(i) })))

const shortMoney = (n) => {
  if (n >= 1_000_000) return `$${+(n / 1_000_000).toFixed(1)} M`
  if (n >= 1_000) return `$${+(n / 1_000).toFixed(1)} k`
  return `$${Math.round(n)}`
}

onMounted(async () => {
  try {
    data.value = await getDashboard()
  } catch (e) {
    loadError.value = errorText(e)
  }
})
</script>

<style scoped>
.kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 14px; }
.metric { padding: 16px 18px; display: flex; flex-direction: column; gap: 4px; text-decoration: none; transition: box-shadow 0.15s; }
.metric--link:hover { box-shadow: 0 2px 8px rgba(30, 58, 95, 0.16); }
.metric__label { font: 600 11px Inter, sans-serif; letter-spacing: 0.04em; text-transform: uppercase; color: #64748b; }
.metric__value { font-size: 24px; font-weight: 700; line-height: 1.2; }
.metric__value--display { font-family: var(--font-display); }
.metric__sub { font: 400 11px Inter, sans-serif; color: #94a3b8; min-height: 14px; }

.chart-card { padding-bottom: 8px; }
.chart-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 18px 4px; }
.sec-title { margin: 0; font: 700 15px var(--font-display); color: #1e3a5f; }
.legend { display: flex; gap: 14px; font: 500 11px Inter, sans-serif; color: #64748b; }
.legend__item { display: inline-flex; align-items: center; gap: 6px; }
.legend__swatch { display: inline-block; }
.legend__swatch--bar { width: 10px; height: 10px; border-radius: 2px; background: #93c5fd; }
.legend__swatch--line { width: 14px; height: 3px; border-radius: 2px; background: #1e3a5f; }
.see-all { font: 600 12px Inter, sans-serif; color: #3b82f6; text-decoration: none; }
.see-all:hover { text-decoration: underline; }

.chart { width: 100%; height: auto; display: block; padding: 0 8px; box-sizing: border-box; }
.grid { stroke: #eef2f7; stroke-width: 1; }
.axis { font: 400 10px Inter, sans-serif; fill: #94a3b8; }
.axis--parks { fill: #1e3a5f; }
.bar { fill: #93c5fd; }
.line { fill: none; stroke: #1e3a5f; stroke-width: 2.5; stroke-linejoin: round; stroke-linecap: round; }
.dot { fill: #fff; stroke: #1e3a5f; stroke-width: 2; }
.chart-note { margin: 0; padding: 0 18px 10px; }

.activity { list-style: none; margin: 0; padding: 4px 0 6px; }
.activity__row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 18px; border-top: 1px solid #f1f5f9; }
.activity__row:first-child { border-top: none; }
.activity__main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.activity__summary { font: 500 13px Inter, sans-serif; color: #1f2937; overflow: hidden; text-overflow: ellipsis; }
.activity__meta { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.activity__time { font: 400 11px Inter, sans-serif; color: #94a3b8; white-space: nowrap; }
</style>